import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const navigationItems = [
  { id: 'platform', label: 'Plataforma' },
  { id: 'how-it-works', label: 'Como Funciona' },
  { id: 'use-cases', label: 'Casos de Uso' },
  { id: 'pricing', label: 'Preços' },
  { id: 'company', label: 'Empresa' },
];

const missionFlow = [
  {
    title: 'Missão',
    description: 'A empresa informa o resultado que precisa conquistar, com prazo, contexto e limite operacional.',
  },
  {
    title: 'Diagnóstico',
    description: 'O LeadNexia analisa histórico, operação, gargalos, riscos e oportunidades antes de agir.',
  },
  {
    title: 'Plano',
    description: 'A plataforma organiza hipóteses, frentes de execução, canais e critérios de sucesso.',
  },
  {
    title: 'Execução',
    description: 'Campanhas, follow-up, CRM e canais passam a operar em função da missão definida.',
  },
  {
    title: 'Monitoramento',
    description: 'Resultados, sinais de saturação, aprovações pendentes e desvios ficam sob leitura contínua.',
  },
  {
    title: 'Ajuste',
    description: 'A estratégia muda com rapidez quando a realidade mostra nova prioridade ou novo risco.',
  },
  {
    title: 'Resultado',
    description: 'A operação fecha o ciclo com ROI, aprendizado e memória para a próxima decisão.',
  },
];

const responsibilities = [
  'Entende sua meta antes de executar.',
  'Diagnostica sua operação comercial.',
  'Identifica gargalos e alavancas reais.',
  'Cria estratégias orientadas a resultado.',
  'Coordena campanhas e follow-up em contexto.',
  'Solicita aprovações quando necessário.',
  'Aprende continuamente com a execução.',
  'Mede impacto, ROI e governança da missão.',
];

const platformCapabilities = [
  'CRM',
  'WhatsApp',
  'Instagram',
  'Facebook',
  'Email',
  'Templates',
  'Campanhas',
  'Follow-up',
  'Relatórios',
  'Integrações',
];

const useCases = [
  {
    title: 'Consórcios',
    description: 'Coordena captação, abordagem, nutrição e retomada de oportunidades para acelerar fechamento.',
  },
  {
    title: 'Imobiliárias',
    description: 'Orquestra oferta, atendimento, follow-up e redistribuição de esforço conforme conversão real.',
  },
  {
    title: 'Clínicas',
    description: 'Transforma metas de agenda, retorno e reativação em operação comercial coordenada.',
  },
  {
    title: 'Franquias',
    description: 'Padroniza inteligência, aprovações e execução entre unidades sem perder autonomia local.',
  },
  {
    title: 'Escritórios',
    description: 'Estrutura prospecção, relacionamento e pipeline com narrativa executiva para decisão rápida.',
  },
  {
    title: 'Indústrias e distribuidores',
    description: 'Conecta canais, carteira, recorrência e expansão comercial em uma única leitura operacional.',
  },
];

const pricingCards = [
  {
    name: 'Growth',
    summary: 'Para operações que precisam trocar ferramentas por coordenação.',
    highlight: false,
    points: [
      'Missões ativas essenciais',
      'Usuários operacionais e gestores',
      'Canais e integrações básicas',
      'Analytics operacional',
      'Aprovações padrão',
    ],
  },
  {
    name: 'Performance',
    summary: 'Para equipes que exigem inteligência comercial como sistema de trabalho.',
    highlight: true,
    points: [
      'Mais missões simultâneas',
      'Memória comercial ativa',
      'Automação e autonomia ampliadas',
      'Governança de aprovações',
      'Análise executiva de ROI',
    ],
  },
  {
    name: 'Enterprise',
    summary: 'Para operações complexas, multicanal e multiunidade.',
    highlight: false,
    points: [
      'Missões em escala',
      'Delegação e políticas avançadas',
      'Integrações enterprise',
      'Analytics e compliance ampliados',
      'Acompanhamento estratégico dedicado',
    ],
  },
];

const commandCenterSignals = [
  { label: 'Chance de sucesso', value: '78%' },
  { label: 'ROI estimado', value: '4.6x' },
  { label: 'Risco principal', value: 'Follow-up lento' },
  { label: 'Aprovações', value: '2 pendentes' },
];

const operationLayers = [
  'CRM',
  'Campanhas',
  'WhatsApp',
  'Instagram',
  'Facebook',
  'Email',
];

const commandTimeline = [
  'Diagnóstico concluiu que a meta exige foco em oficinas premium.',
  'Plano redistribuiu esforço de captação para reativação de carteira.',
  'Nova campanha entrou em execução com aprovação comercial.',
  'Sistema sinalizou atraso em resposta e elevou risco da missão.',
];

function CommandCenterMock() {
  return (
    <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-slate-950/70 p-4 shadow-2xl shadow-slate-950/40 backdrop-blur">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.22),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(243,115,53,0.16),transparent_30%)]" />
      <div className="relative space-y-4">
        <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.24em] text-sky-100/90">Missão atual</p>
            <h3 className="mt-2 text-lg font-semibold text-white">Expandir Brasília com R$ 500 mil em consórcios</h3>
          </div>
          <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-300">
            Plano em execução
          </span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {commandCenterSignals.map((signal) => (
            <div key={signal.label} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
              <p className="text-xs text-slate-200">{signal.label}</p>
              <p className="mt-2 text-xl font-semibold text-white">{signal.value}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-3 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-white">Próximas ações</p>
              <span className="text-xs text-slate-200">24 horas</span>
            </div>
            <div className="mt-4 space-y-3">
              {[
                'Intensificar abordagem de oficinas com maior ticket.',
                'Solicitar aprovação para verba adicional em reativação.',
                'Redistribuir carteira parada para operador sênior.',
              ].map((item) => (
                <div key={item} className="flex gap-3 rounded-xl border border-white/6 bg-slate-900/70 px-3 py-3">
                  <span className="mt-1 h-2.5 w-2.5 rounded-full bg-sky-400" />
                  <p className="text-sm leading-6 text-slate-200">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
            <p className="text-sm font-semibold text-white">Riscos e oportunidades</p>
            <div className="mt-4 space-y-3">
              <div className="rounded-xl border border-amber-400/20 bg-amber-400/10 p-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-200">Risco</p>
                <p className="mt-2 text-sm text-amber-100">Tempo de resposta da equipe abaixo do ideal nas últimas 48 horas.</p>
              </div>
              <div className="rounded-xl border border-sky-400/20 bg-sky-400/10 p-3">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-200">Oportunidade</p>
                <p className="mt-2 text-sm text-sky-100">Canal de indicação supera a média de conversão nesta missão.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="fixed inset-x-0 top-0 z-50">
        <nav
          className={`mx-auto mt-4 max-w-[88rem] rounded-[28px] border px-4 transition-all duration-300 sm:px-6 lg:px-8 ${
            scrolled
              ? 'border-slate-200/15 bg-slate-950/85 shadow-2xl shadow-slate-950/30 backdrop-blur'
              : 'border-white/10 bg-white/[0.04] backdrop-blur'
          }`}
        >
          <div className="flex h-[76px] items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#0ea5e9,#1d4ed8_65%,#f37335)] shadow-lg shadow-sky-950/40">
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 3L4 14h7v7l9-11h-7V3z" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="text-base font-semibold text-white">LeadNexia</p>
                <p className="text-[11px] uppercase tracking-[0.24em] text-slate-300">Commercial Intelligence</p>
              </div>
            </Link>

            <div className="hidden flex-1 items-center justify-center px-4 xl:flex">
              <div className="flex items-center gap-2 rounded-full border border-white/8 bg-white/[0.03] px-3 py-2">
                {navigationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="rounded-full px-4 py-2 text-sm text-slate-300 transition hover:bg-white/[0.06] hover:text-white"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="hidden items-center gap-3 lg:flex xl:hidden">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-sm text-slate-300 transition hover:text-white"
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="hidden items-center gap-3 lg:flex">
              <Link to="/login" className="rounded-full border border-white/10 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-white/20 hover:bg-white/5">
                Entrar
              </Link>
              <Link to="/cadastro" className="rounded-full bg-[linear-gradient(135deg,#38bdf8,#2563eb_60%,#f37335)] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-950/40 transition hover:scale-[1.02]">
                Começar Agora
              </Link>
            </div>

            <button
              className="rounded-full border border-white/10 p-2 text-slate-200 lg:hidden"
              onClick={() => setMobileMenuOpen((value) => !value)}
              aria-label="Abrir menu"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {mobileMenuOpen ? (
            <div className="space-y-2 border-t border-white/10 pb-4 pt-3 lg:hidden">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="block w-full rounded-2xl px-3 py-2 text-left text-sm text-slate-300 transition hover:bg-white/5 hover:text-white"
                >
                  {item.label}
                </button>
              ))}
              <div className="grid gap-2 pt-2">
                <Link to="/login" className="rounded-2xl border border-white/10 px-4 py-2.5 text-center text-sm font-medium text-slate-100">
                  Entrar
                </Link>
                <Link to="/cadastro" className="rounded-2xl bg-[linear-gradient(135deg,#38bdf8,#2563eb_60%,#f37335)] px-4 py-2.5 text-center text-sm font-semibold text-white">
                  Começar Agora
                </Link>
              </div>
            </div>
          ) : null}
        </nav>
      </div>

      <main>
        <section className="relative overflow-hidden border-b border-white/10 bg-[linear-gradient(180deg,#020617_0%,#06142d_38%,#0f2d63_100%)] pt-28">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(56,189,248,0.16),transparent_22%),radial-gradient(circle_at_85%_18%,rgba(29,78,216,0.22),transparent_22%),radial-gradient(circle_at_80%_80%,rgba(243,115,53,0.10),transparent_20%)]" />
          <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)', backgroundSize: '72px 72px' }} />

          <div className="relative mx-auto grid max-w-7xl gap-16 px-4 pb-20 pt-10 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:pb-28 lg:pt-16">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/20 bg-sky-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-sky-100">
                Inteligência Comercial para operações orientadas a resultado
              </div>

              <h1 className="mt-8 text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl">
                Sua operação comercial não precisa de mais ferramentas.
                <span className="mt-3 block bg-[linear-gradient(135deg,#ffffff,#7dd3fc_35%,#fba96a_85%)] bg-clip-text text-transparent">
                  Precisa de uma inteligência que coordene resultados.
                </span>
              </h1>

              <p className="mt-8 max-w-2xl text-lg leading-8 text-slate-200">
                O LeadNexia transforma metas comerciais em Missões inteligentes. Diagnostica sua operação,
                cria estratégias, coordena campanhas, acompanha resultados e adapta continuamente o plano
                para aumentar suas vendas.
              </p>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row">
                <Link to="/cadastro" className="inline-flex items-center justify-center rounded-full bg-[linear-gradient(135deg,#38bdf8,#2563eb_60%,#f37335)] px-7 py-4 text-base font-semibold text-white shadow-2xl shadow-sky-950/40 transition hover:scale-[1.02]">
                  Criar minha primeira missão
                </Link>
                <Link to="/login" className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-7 py-4 text-base font-medium text-white transition hover:bg-white/10">
                  Agendar demonstração
                </Link>
              </div>

              <div className="mt-10 grid gap-3 sm:grid-cols-3">
                {[
                  ['Não é apenas CRM', 'Entrega visão executiva e coordenação operacional.'],
                  ['Não é ferramenta de disparo', 'Executa em contexto, com critério e supervisão.'],
                  ['Não é só WhatsApp', 'Coordena canais, memória e estratégia em uma única leitura.'],
                ].map(([title, description]) => (
                  <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                    <p className="text-sm font-semibold text-white">{title}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-200">{description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:pt-10">
              <CommandCenterMock />
            </div>
          </div>
        </section>

        <section id="platform" className="border-b border-white/10 bg-slate-950 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-300">O problema</p>
                <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl">
                  As empresas ainda gerenciam ferramentas quando deveriam gerenciar resultado.
                </h2>
                <p className="mt-6 max-w-xl text-base leading-8 text-slate-200">
                  Hoje o gestor comercial salta entre CRM, WhatsApp, campanhas, planilhas e relatórios para
                  tentar entender o que está acontecendo. O custo não é apenas operacional. É estratégico.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  ['CRM', 'Dados dispersos e leitura operacional sem síntese executiva.'],
                  ['Campanhas', 'Execução isolada, sem relação clara com a meta principal.'],
                  ['WhatsApp', 'Canal protagonista quando deveria ser apenas um meio.'],
                  ['Planilhas', 'Análises manuais, lentas e pouco governadas.'],
                  ['Relatórios', 'Visão retroativa em vez de coordenação contínua.'],
                  ['Decisões', 'Dependem da intuição de quem consegue juntar as peças.'],
                ].map(([title, description]) => (
                  <div key={title} className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                    <p className="text-sm font-semibold text-white">{title}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-200">{description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-16 rounded-[32px] border border-sky-400/15 bg-[linear-gradient(135deg,rgba(14,165,233,0.08),rgba(15,23,42,0.2)_45%,rgba(243,115,53,0.08))] p-8">
              <div className="grid gap-6 lg:grid-cols-[1fr_auto_1fr] lg:items-center">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">Modelo tradicional</p>
                  <div className="mt-4 space-y-3">
                    {['CRM', 'Campanhas', 'Disparos', 'Relatórios', 'Decisões'].map((item) => (
                      <div key={item} className="rounded-2xl border border-white/8 bg-slate-950/70 px-4 py-3 text-sm text-slate-300">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-sky-300">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-4-4l4 4-4 4" />
                  </svg>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">LeadNexia</p>
                  <div className="mt-4 space-y-3">
                    {['Missão', 'Diagnóstico', 'Plano', 'Execução', 'Resultados', 'Aprendizado'].map((item) => (
                      <div key={item} className="rounded-2xl border border-sky-400/20 bg-sky-400/10 px-4 py-3 text-sm font-medium text-sky-50">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-white/10 bg-slate-900 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[1fr_1.05fr]">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-300">A nova forma de trabalhar</p>
                <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl">
                  O LeadNexia coordena toda a operação comercial.
                </h2>
                <p className="mt-6 max-w-xl text-base leading-8 text-slate-200">
                  CRM, campanhas, WhatsApp, Instagram, Facebook e Email continuam existindo. A diferença
                  é que agora eles deixam de ser o centro da experiência e passam a trabalhar para cumprir
                  uma Missão.
                </p>
              </div>

              <div className="rounded-[32px] border border-white/10 bg-slate-950 p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-300">Camadas da operação</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {operationLayers.map((layer) => (
                    <span key={layer} className="rounded-full border border-white/10 bg-white/[0.05] px-4 py-2 text-sm text-slate-200">
                      {layer}
                    </span>
                  ))}
                </div>
                <div className="mt-8 rounded-3xl border border-sky-400/20 bg-sky-400/10 p-6">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-200">Objetivo comum</p>
                  <h3 className="mt-3 text-2xl font-semibold text-white">Cumprir a missão com critério, velocidade e governança.</h3>
                  <p className="mt-3 text-sm leading-7 text-sky-50/90">
                    O gestor supervisiona o resultado. O LeadNexia organiza a operação para que os meios sirvam ao fim.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="border-b border-white/10 bg-slate-950 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-300">Como funciona</p>
              <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl">
                Um fluxo contínuo de diagnóstico, execução e aprendizado.
              </h2>
              <p className="mt-6 text-base leading-8 text-slate-200">
                O produto foi desenhado para esconder a complexidade operacional e expor apenas o que importa para a tomada de decisão.
              </p>
            </div>

            <div className="mt-12 grid gap-4 lg:grid-cols-7">
              {missionFlow.map((step, index) => (
                <div key={step.title} className="relative rounded-3xl border border-white/10 bg-white/[0.03] p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {index < missionFlow.length - 1 ? <span className="hidden text-slate-300 lg:block">→</span> : null}
                  </div>
                  <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-200">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-white/10 bg-[linear-gradient(180deg,#020617_0%,#081224_100%)] py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-300">Centro de Comando</p>
                <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl">
                  A nova tela principal da operação comercial.
                </h2>
                <p className="mt-6 max-w-xl text-base leading-8 text-slate-200">
                  Em vez de navegar entre dezenas de módulos, o gestor acompanha a Missão Atual, progresso,
                  chance de sucesso, riscos, oportunidades, timeline, próximas ações, pendências e ROI.
                </p>

                <div className="mt-8 grid gap-3">
                  {[
                    'Missão atual e progresso em um único contexto.',
                    'Chance de sucesso acompanhada por justificativa.',
                    'Pendências e aprovações sem perder a operação de vista.',
                    'Timeline viva do que mudou e por que mudou.',
                  ].map((item) => (
                    <div key={item} className="flex gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                      <span className="mt-1 h-2.5 w-2.5 rounded-full bg-sky-400" />
                      <p className="text-sm leading-6 text-slate-300">{item}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-slate-950/40">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-3xl border border-white/8 bg-white/[0.04] p-5 sm:col-span-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Missão atual</p>
                    <h3 className="mt-3 text-2xl font-semibold text-white">Reativar carteira premium com ROI acima de 3x</h3>
                    <p className="mt-3 text-sm leading-7 text-slate-200">Plano em andamento com priorização em segmentos historicamente mais rentáveis.</p>
                  </div>

                  <div className="rounded-3xl border border-white/8 bg-white/[0.04] p-5">
                    <p className="text-sm text-slate-200">Chance de sucesso</p>
                    <p className="mt-3 text-3xl font-semibold text-white">81%</p>
                  </div>
                  <div className="rounded-3xl border border-white/8 bg-white/[0.04] p-5">
                    <p className="text-sm text-slate-200">Progresso</p>
                    <p className="mt-3 text-3xl font-semibold text-white">63%</p>
                  </div>

                  <div className="rounded-3xl border border-white/8 bg-white/[0.04] p-5">
                    <p className="text-sm font-semibold text-white">Timeline</p>
                    <div className="mt-4 space-y-3">
                      {commandTimeline.map((item) => (
                        <div key={item} className="flex gap-3">
                          <span className="mt-2 h-2 w-2 rounded-full bg-sky-400" />
                          <p className="text-sm leading-6 text-slate-200">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-white/8 bg-white/[0.04] p-5">
                    <p className="text-sm font-semibold text-white">Pendências</p>
                    <div className="mt-4 space-y-3">
                      {[
                        'Aprovar nova verba de reativação.',
                        'Validar abordagem para carteira inativa.',
                        'Delegar follow-up premium para gestor regional.',
                      ].map((item) => (
                        <div key={item} className="rounded-2xl border border-white/6 bg-slate-900/80 px-3 py-3 text-sm text-slate-300">
                          {item}
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-200">ROI</p>
                      <p className="mt-2 text-2xl font-semibold text-white">4.1x projetado</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-white/10 bg-slate-950 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-300">Responsabilidades do LeadNexia</p>
                <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl">
                  O produto não vende funcionalidades. Ele assume responsabilidades.
                </h2>
              </div>

              <div className="grid gap-3">
                {responsibilities.map((item) => (
                  <div key={item} className="flex gap-4 rounded-3xl border border-white/10 bg-white/[0.03] px-5 py-4">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-400/15 text-sky-300">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm leading-7 text-slate-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-white/10 bg-slate-900 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-300">Capacidades da plataforma</p>
              <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl">
                Tudo o que já existe na operação continua disponível. Só deixa de ser protagonista.
              </h2>
              <p className="mt-6 text-base leading-8 text-slate-200">
                CRM, canais, templates e campanhas passam a ser capacidades usadas pelo LeadNexia para cumprir Missões.
              </p>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              {platformCapabilities.map((capability) => (
                <div key={capability} className="rounded-3xl border border-white/10 bg-white/[0.03] px-5 py-4">
                  <p className="text-sm font-medium text-white">{capability}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="use-cases" className="border-b border-white/10 bg-slate-950 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-300">Casos de uso</p>
              <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl">
                Aplicações reais para empresas que precisam coordenar receita com mais inteligência.
              </h2>
            </div>

            <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {useCases.map((item) => (
                <div key={item.title} className="rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-6">
                  <p className="text-lg font-semibold text-white">{item.title}</p>
                  <p className="mt-3 text-sm leading-7 text-slate-200">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="border-b border-white/10 bg-slate-900 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-300">Planos</p>
              <h2 className="mt-4 text-3xl font-semibold text-white sm:text-4xl">
                Os planos deixam de vender volume de mensagens. Passam a vender capacidade operacional.
              </h2>
              <p className="mt-6 text-base leading-8 text-slate-200">
                O dimensionamento combina missões ativas, usuários, inteligência comercial, memória, integrações, autonomia, canais, analytics e aprovações.
              </p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {pricingCards.map((plan) => (
                <div
                  key={plan.name}
                  className={`rounded-[32px] border p-8 ${
                    plan.highlight
                      ? 'border-sky-400/30 bg-[linear-gradient(180deg,rgba(14,165,233,0.14),rgba(15,23,42,0.96))] shadow-2xl shadow-sky-950/30'
                      : 'border-white/10 bg-white/[0.03]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-2xl font-semibold text-white">{plan.name}</p>
                    {plan.highlight ? (
                      <span className="rounded-full border border-sky-300/30 bg-sky-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-sky-100">
                        Recomendado
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-4 text-sm leading-7 text-slate-200">{plan.summary}</p>

                  <div className="mt-8 space-y-3">
                    {plan.points.map((point) => (
                      <div key={point} className="flex gap-3">
                        <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-sky-400" />
                        <p className="text-sm leading-6 text-slate-200">{point}</p>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/cadastro"
                    className={`mt-8 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition ${
                      plan.highlight
                        ? 'bg-[linear-gradient(135deg,#38bdf8,#2563eb_60%,#f37335)] text-white hover:scale-[1.01]'
                        : 'border border-white/12 bg-white/[0.04] text-white hover:bg-white/[0.08]'
                    }`}
                  >
                    Começar Agora
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="company" className="bg-[linear-gradient(180deg,#020617_0%,#0f2d63_100%)] py-24">
          <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-200">LeadNexia</p>
            <h2 className="mt-4 text-4xl font-semibold text-white sm:text-5xl">
              Pronto para transformar sua operação comercial?
            </h2>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-slate-200/90">
              Enquanto sua equipe vende, o LeadNexia coordena, aprende e melhora continuamente toda a operação.
            </p>
            <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
              <Link to="/cadastro" className="inline-flex items-center justify-center rounded-full bg-[linear-gradient(135deg,#38bdf8,#2563eb_60%,#f37335)] px-8 py-4 text-base font-semibold text-white shadow-2xl shadow-sky-950/40 transition hover:scale-[1.02]">
                Criar minha primeira missão
              </Link>
              <Link to="/login" className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-8 py-4 text-base font-medium text-white transition hover:bg-white/10">
                Entrar
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-slate-950">
        <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 py-10 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#0ea5e9,#1d4ed8_65%,#f37335)]">
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 3L4 14h7v7l9-11h-7V3z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-white">LeadNexia</p>
                <p className="text-sm text-slate-300">Inteligência comercial para operações orientadas a missão.</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-6 text-sm text-slate-300">
            {navigationItems.map((item) => (
              <button key={item.id} onClick={() => scrollToSection(item.id)} className="transition hover:text-white">
                {item.label}
              </button>
            ))}
            <Link to="/login" className="transition hover:text-white">Entrar</Link>
            <Link to="/cadastro" className="font-medium text-sky-300 transition hover:text-sky-200">Começar Agora</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
